import React, { Suspense, useEffect, useState } from 'react';
import clsx from 'clsx';
import { useLocation, useNavigate } from 'react-router-dom';
import { of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { HdResolvedComponent } from '../../routing/components/resolved-component/HdResolvedComponent';
import { GenericPageShimmer } from '../../loader/components/generic-page-shimmer/GenericPageShimmer';
import { useAuthGuard } from '../../auth/hooks/useAuthGuard';
import { AccountAccessService } from '../../core/services/account-access.service';
import { AllowedActionsService } from '../../core/services/entity-actions.service';
import { MultiRegionService } from '../../auth/services/multi-region.service';
import { RecentEntitiesService } from '../../core/services/recent-entities.service';
import { TeamSettingsService } from '../../core/services/team-settings.service';
import { TeamService } from '../../core/services/team.service';
import { KeyboardShortcuts } from '../../a11y/shortcuts/service/keyboard-shortcuts.service';
import { ShortcutsDialogHandlerReactService } from '../../a11y/shortcuts/service/shortcuts-dialog-handler-react.service';
import { AngularDiProviders } from '../../injector/components/angular-di/AngularDiProviders';
import { ShareDocDialogService } from '../../containers/team/InviteMembersDialog/share-doc-dialog.service';
import { DashboardBanners } from './dashboard-banners/DashboardBanners';
import { useAlertDialog } from '../../modal/hooks/useAlertDialog';
import SourceInviteMembersDialog from '../../containers/team/InviteMembersDialog/SourceInviteMembersDialog';
import { useKeyboardShortcutListeners } from '../../a11y/shortcuts/hooks/useKeyboardShortcutListeners';
import { useObservableState } from '../../core/hooks/useObservableState';
import useService from '../../injector/hooks/useService';
import { useSubscribe } from '../../core/hooks/useSubscribe';
import { DELAY_BEFORE_DELETING_ACCOUNT } from '../../core/constants/constants';
import { AuthService } from '../../auth/services/auth.service';
import { OverageDataService } from '../../core/services/overage-data.service';
import { PlanChatSupportService } from '../../support/services/plan-chat-support.service';
import { ContactSupportAIService } from '../../containers/contact-support-ai/services/contact-support-ai.service';
import { TrialDetailsService } from '../../core/services/trial-details.service';
import { UserTimezoneService } from '../../core/services/user-timezone.service';
import { DrawersContainer } from '../../drawer/components/drawers-container/DrawersContainer';
import { PCNASupportService } from '../../containers/pipeline/services/pcna-support.service';
import { GlobalSearchDialog } from '../../global-search/components/GlobalSearchDialog/GlobalSearchDialog';
import ShortcutsDialog from '../../a11y/shortcuts/components/shortcuts-dialog/ShortcutsDialog';
import { IconChevronLeft, IconChevronRight } from '@tabler/icons-react';
import { Appbar } from './appbar/Appbar';
import TopBar from './top-bar/Topbar';
import styles from './Dashboard.module.scss';
import { usePostLoginDataUpdateGuard } from '../../auth/hooks/usePostLoginDataUpdateGuard';
import { DashboardRoutes } from './DashboardRoutes';
import ContactSupportAI from '../../containers/contact-support-ai/components/ContactSupportAI';
import { useExitIntent } from '../../edge/pipelines/PipelineList/components/PipelineIntro/useExitIntent';
import { ScheduleDemoModal } from '../../edge/pipelines/PipelineList/components/PipelineIntro/ScheduleDemoModal';
import { GlobalAttentionItemsModal } from '../../edge/notifications/attention-modal/GlobalAttentionItemsModal';

const providers = [OverageDataService, ShareDocDialogService, KeyboardShortcuts];

export function Dashboard() {
  return (
    <AngularDiProviders providers={providers}>
      <DashboardWithResolvedData />
    </AngularDiProviders>
  );
}

export function DashboardWithResolvedData() {
  const teamService = useService(TeamService);
  const multiRegionService = useService(MultiRegionService);
  const accountAccessService = useService(AccountAccessService);
  const teamSettingsService = useService(TeamSettingsService);
  const allowedActionsService = useService(AllowedActionsService);
  const recentEntitiesService = useService(RecentEntitiesService);

  const { executePostLoginDataUpdateGuard } = usePostLoginDataUpdateGuard();
  const { executeAuthGuard } = useAuthGuard();

  const getTeamDetails = () => teamService
      .getTeamDetails()
      .pipe(
        map((res: any) => res.data),
        catchError((err: any) => of(null))
      )
      .toPromise();

  const getMultiRegions = () => multiRegionService
      .getMultiRegions(false)
      .pipe(
        map((res: any) => res.data),
        catchError((err: any) => of(null))
      )
      .toPromise();

  const executeTeamSettingsGuard = () => teamSettingsService
      .getTeamSettings(false, false, false)
      .pipe(
        map(() => true),
        catchError(() => of(true))
      )
      .toPromise();

  const fetchAccountAccessConsentStatus = () => accountAccessService
      .fetchAccountAccessConsentStatus()
      .pipe(
        map((res: any) => res.data),
        catchError((err: any) => of(null))
      )
      .toPromise();

  const executeFetchGlobalActionsGuard = () => allowedActionsService
      .getAllowedActions(true, false)
      .pipe(
        map(() => true),
        catchError(() => of(true))
      )
      .toPromise();

  const getRecentEntities = () => recentEntitiesService.fetchRecent().pipe(
      catchError(() => of(null))
    ).toPromise();

  return (
    <HdResolvedComponent
      parallelExecution
      Component={DashboardInner}
      resolve={{
        recentEntities: getRecentEntities,
        teamDetails: getTeamDetails,
        multiRegion: getMultiRegions,
        accountAccess: fetchAccountAccessConsentStatus
      }}
      canActivate={[
        executeAuthGuard,
        executeTeamSettingsGuard,
        executeFetchGlobalActionsGuard,
        executePostLoginDataUpdateGuard
      ]}
    />
  );
}

export function DashboardInner() {
  const { alert } = useAlertDialog();

  const [isNavExpanded, setIsNavExpanded] = useState(() => {
    try { return localStorage.getItem('hevo_nav_expanded') === 'true'; } catch { return false; }
  });

  const toggleNav = () => {
    setIsNavExpanded(prev => {
      const next = !prev;
      try { localStorage.setItem('hevo_nav_expanded', String(next)); } catch {}
      return next;
    });
  };
  /** Needed to initialise */
  useService(KeyboardShortcuts);
  const shareDocDialogService = useService(ShareDocDialogService);
  const trialDetailsService = useService(TrialDetailsService);
  const authService = useService(AuthService);
  const shortcutsDialogHandlerReactService = useService(ShortcutsDialogHandlerReactService);
  const planChatSupportService = useService(PlanChatSupportService);
  const contactSupportAIService = useService(ContactSupportAIService);
  const userTimezoneService = useService(UserTimezoneService);
  const pcnaSupportService = useService(PCNASupportService);
  const teamService = useService(TeamService);

  const navigate = useNavigate();
  const location = useLocation();

  const redirectOnHomePageNav = () => {
    const currentUrl = new URL(window.location.href);

    if (currentUrl.pathname === '/') {
      let redirectUrl = '/pipeline';

      const {teamDetails} = teamService;

      if (teamDetails?.sign_up_source === 'ACTIVATE') {
        redirectUrl = '/activation';
      }

      navigate(redirectUrl, {
        replace: true
      });
    }
  };

  useEffect(() => {
    redirectOnHomePageNav();
  }, [location]);

  useSubscribe(
    trialDetailsService.accountDeleted$.pipe(
      tap(() => {
        alert({
          dataId: '',
          title: 'You will be logged out soon',
          body: 'Your Hevo account has been deleted. You will be logged out soon'
        }).then();

        setTimeout(() => {
          authService.logout().subscribe();
        }, DELAY_BEFORE_DELETING_ACCOUNT);
      })
    ),
    []
  );

  useSubscribe(userTimezoneService.setDefaultTimezone(), []);

  useSubscribe(planChatSupportService.liveChatDisabled$, []);

  useSubscribe(authService.pollUserData(), []);

  useKeyboardShortcutListeners(
    {
      'global.keyboardShortcuts': (event: KeyboardEvent) => {
        shortcutsDialogHandlerReactService.closeGlobalSearchDialog();
        shortcutsDialogHandlerReactService.openKeyboardShortcutDialog();
        event.preventDefault();
      },
      'global.searchDialog': (event: KeyboardEvent) => {
        shortcutsDialogHandlerReactService.closeKeyboardShortcutDialog();
        shortcutsDialogHandlerReactService.openGlobalSearchDialog();
        event.preventDefault();
      }
    },
    {
      priority: 0,
      terminal: 'match'
    }
  );

  useEffect(() => {
    pcnaSupportService.init();

    return () => {
      planChatSupportService.reset();
    };
  }, []);

  const [isShareDocDialogVisible] = useObservableState(shareDocDialogService.isDialogVisible$, false);
  const [isContactSupportAIOpen] = useObservableState(contactSupportAIService.isOpen$);
  const { showModal: showScheduleDemoModal, dismissModal: dismissScheduleDemoModal } = useExitIntent();

  return (
    <div className='flex-col h-100'>
      <DashboardBanners />
      <ShortcutsDialog />
      <GlobalSearchDialog />
      {isContactSupportAIOpen ? 
          <ContactSupportAI
            onClose={() => contactSupportAIService.hide()}
          />
        : null
      }

      <div className={styles.dashboardContainer}>
        <div className={styles.sidebarArea}>
          <Appbar expanded={isNavExpanded} />

          <button
            className={clsx(styles.sidebarToggleBtn, isNavExpanded && styles.sidebarToggleBtnExpanded)}
            onClick={toggleNav}
            title={isNavExpanded ? 'Collapse sidebar' : 'Expand sidebar'}
            data-id='nav-toggle'
          >
            {isNavExpanded
              ? <IconChevronLeft size={16} strokeWidth={2} />
              : <IconChevronRight size={16} strokeWidth={2} />
            }
          </button>
        </div>

        <div className={clsx(styles.appBody, isNavExpanded && styles.appBodyExpanded)}>
          <TopBar />

          <Suspense fallback={<GenericPageShimmer />}>
            <DashboardRoutes />
          </Suspense>
        </div>

        <DrawersContainer />

        <ScheduleDemoModal open={showScheduleDemoModal} onClose={dismissScheduleDemoModal} />

        <GlobalAttentionItemsModal />

        <SourceInviteMembersDialog
          renderAsDialog
          open={isShareDocDialogVisible}
          onClose={() => shareDocDialogService.hide()}
          docLink={shareDocDialogService.dialogOptions.docLink}
          entity={shareDocDialogService.dialogOptions.entity}
          sourceId={shareDocDialogService.dialogOptions.sourceId}
          sourceDisplayName={shareDocDialogService.dialogOptions.displayName}
          type={shareDocDialogService.dialogOptions.type}
        />
      </div>
    </div>
  );
}
